import { ArrowUp, KeyRound, MapPin, VolumeX } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";

import { CommandButton } from "../components/CommandButton";
import { StatusBlock } from "../components/StatusBlock";
import { useAudioCoordinator } from "../stores/audioCoordinator";
import { useSessionStore } from "../stores/sessionStore";
import styles from "../styles/App.module.css";

export function ActivationPage() {
  const stopAll = useAudioCoordinator((state) => state.stopAll);
  const announce = useAudioCoordinator((state) => state.announce);
  const message = useAudioCoordinator((state) => state.message);
  const activate = useSessionStore((state) => state.activate);
  const error = useSessionStore((state) => state.error);
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const activatingRef = useRef(false);
  const [token, setToken] = useState("");
  const [localError, setLocalError] = useState("");
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    document.title = "输入激活码 - 我的听戏";
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!/^\d{6}$/.test(token)) {
      setLocalError("请输入 6 位数字激活码。");
      return;
    }

    if (activatingRef.current) {
      setLocalError("");
      setActivating(true);
      return;
    }

    activatingRef.current = true;
    setActivating(true);
    try {
      setLocalError("");
      await activate(token);
    } catch (activationError) {
      setLocalError(
        activationError instanceof Error
          ? activationError.message
          : "激活失败。",
      );
    } finally {
      activatingRef.current = false;
      setActivating(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>我的听戏</p>
        <h1>输入激活码</h1>
      </header>
      <main className={styles.main}>
        <form className={styles.activateForm} onSubmit={submit}>
          <label htmlFor="activation-token">6 位数字激活码</label>
          <input
            id="activation-token"
            ref={tokenInputRef}
            aria-label="请输入 6 位数字激活码"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]{6}"
            value={token}
            onChange={(event) =>
              setToken(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
          />
          <CommandButton
            icon={KeyRound}
            title="完成激活"
            hint={activating ? "正在激活，请稍等" : undefined}
            type="submit"
          />
        </form>
        <StatusBlock
          tone={localError || error ? "error" : "info"}
          message={
            activating
              ? "正在激活，请稍等。"
              : localError || error || "请输入家人电话告知的 6 位激活码。"
          }
          recoveryAction={
            localError || error
              ? "请重新输入 6 位数字，或请家人重新生成激活码。"
              : undefined
          }
        />
        <section
          className={styles.plainSection}
          aria-labelledby="install-title"
        >
          <h2 id="install-title">安装到主屏幕</h2>
          <p>在 iPhone Safari 中打开后，使用分享菜单，选择“添加到主屏幕”。</p>
        </section>
      </main>
      <p className={styles.liveRegion} aria-live="assertive">
        {message}
      </p>
      <nav className={styles.globalControls} aria-label="全局操作">
        <CommandButton
          icon={VolumeX}
          title="停止所有 App 声音"
          onClick={stopAll}
        />
        <CommandButton
          icon={MapPin}
          title="告诉我现在在哪"
          onClick={() => {
            announce("你现在在：激活页。");
          }}
        />
        <CommandButton
          icon={ArrowUp}
          title="回到激活码输入"
          hint="这是本页最后一个按钮"
          onClick={() => tokenInputRef.current?.focus()}
        />
      </nav>
    </div>
  );
}
